import React, {
  ReactElement,
  useState,
  Fragment,
  useCallback,
} from 'react';
import {
  Autocomplete,
  Button as MUIButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { SchemaWithClient } from '../../types/SchemaWithClient';
import { Entity } from '../../types/entities/Entity';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { LoadingButton } from '@mui/lab';
import api from '../../services/api';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import LargePopper from '../Table/assets/LargePopper';
import toast from 'react-hot-toast';

type Props<T extends Entity> = {
  schema: SchemaWithClient<T>;
};
export const AddNewContact = ({
  schema,
  onClose,
  onSubmit,
  serviceProvider,
}: {
  schema: any;
  onClose: () => void;
  onSubmit: () => void;
  serviceProvider: any;
}): ReactElement => {
  const [loading, setLoading] = useState<any>(false);
  const [selected, setSelected] = useState<any>(undefined);
  const [searchName, setSearchName] = useState<any>(undefined);
  const [existingContact, setExistingContact] = useState<any>(false);
  const [options, setOptions] = useState<any>([]);
  const [roleOptions, setRoleOptions] = useState<any>([]);
  const [existingContactModify, setExistingContactModify] = useState<any>(
    false
  );
  const [formData, setFormData] = useState<any>({
    name: undefined,
    email: undefined,
    phone: undefined,
    address: undefined,
    roles: [],
    accommodation: serviceProvider,
  });

  const handleChange = (column: any, value: any) => {
    setFormData({ ...formData, [column]: value });
    if (column === 'name') {
      setSearchName(value);
    }

    if (existingContact) {
      setExistingContactModify(true);
    }
  };

  const fetchContacts = useCallback((searchText: any) => {
    const searchParams = new URLSearchParams({
      name: searchText,
    });

    api.get(`/contacts`, searchParams).then((res: any) => {
      if (res?.data && res.data.length) {
        setOptions(res?.data);
      }
    });
  }, []);

  const fetchRoles = useCallback((searchText: any) => {
    const searchParams = new URLSearchParams({
      name: searchText,
    });

    api.get(`/enums/user-types`, searchParams).then((res: any) => {
      if (res?.data && res.data.length) {
        setRoleOptions(res?.data);
      }
    });
  }, []);

  const send = () => {
    setLoading(true);

    if (existingContact) {
      if (existingContactModify) {
        api
          .patch(`/contacts/${existingContact.id}`, formData)
          .then(() => {
            api
              .post(
                `/add-contact-to-accommodation/${serviceProvider?.id}/${existingContact.id}`,
                {}
              )
              .then(() => {
                onSubmit();
                toast.success(`Successfully added Contact`);
              })
              .catch((error: any) => {
                let array: any = [];
                for (let errorKey in error) {
                  for (let errorMsg in error[errorKey]) {
                    array.push(`${error[errorKey][errorMsg]}`);
                  }
                }
                toast.error(array.join(','));
              })
              .finally(() => {
                setLoading(false);
              });
            toast.success(`Successfully updated Contact`);
          })
          .catch((error: any) => {
            let array: any = [];
            for (let errorKey in error) {
              for (let errorMsg in error[errorKey]) {
                array.push(`${error[errorKey][errorMsg]}`);
              }
            }
            toast.error(array.join(','));
          })
          .finally(() => {
            setLoading(false);
          });

        return;
      }

      api
        .post(
          `/add-contact-to-accommodation/${serviceProvider?.id}/${existingContact.id}`,
          {}
        )
        .then(() => {
          onSubmit();
          toast.success(`Successfully added Contact`);
        })
        .catch((error: any) => {
          let array: any = [];
          for (let errorKey in error) {
            for (let errorMsg in error[errorKey]) {
              array.push(`${error[errorKey][errorMsg]}`);
            }
          }
          toast.error(array.join(','));
        })
        .finally(() => {
          setLoading(false);
        });

      return;
    } else {
      api
        .post(`/contacts`, formData)
        .then((res) => {
          onSubmit();
          toast.success(`Successfully added Contact`);
        })
        .catch((error: any) => {
          let array: any = [];
          for (let errorKey in error) {
            for (let errorMsg in error[errorKey]) {
              array.push(`${error[errorKey][errorMsg]}`);
            }
          }
          toast.error(array.join(','));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
      <Dialog
        fullScreen={fullScreen}
        open
        onClose={(event) => {
          onClose();
        }}
        aria-labelledby="responsive-dialog-title"
        maxWidth={'md'}
        sx={{
          '& .MuiPaper-root': {
            width: {
              md: 'auto',
              lg: 'auto',
            },
            minWidth: {
              md: '600px',
              lg: '600px',
            },

            maxWidth: {
              md: 'calc(100vw - 400px)',
              lg: 'calc(100vw - 400px)',
            },
          },
        }}
      >
        <DialogTitle>{`Add new ${schema.name}`}</DialogTitle>
        <DialogContent
          sx={{
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <Stack
            gap={0}
            sx={{
              borderBottom: '1px solid #dfdbdb',
              borderTop: '1px solid #dfdbdb',
              '& .MuiTypography-root': {
                background: '#f1f1f1',
                p: '8px',
                fontSize: '14px',
              },
              '& .MuiInputBase-root.MuiOutlinedInput-root': {
                borderRadius: '0px',
              },
              '& .MuiInputBase-root.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
              },
              '& .MuiInputBase-root.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#b3cdcae0',
              },
              '& > .MuiStack-root': {
                borderBottom: '1px solid #dfdbdb',
                borderRight: '1px solid #dfdbdb',
              },
            }}
          >
            <Stack direction={'row'}>
              <Typography
                sx={{
                  width: '220px',
                }}
              >
                Full Name
              </Typography>
              <Autocomplete
                freeSolo
                size={'small'}
                fullWidth={true}
                PopperComponent={LargePopper}
                onOpen={() => {
                  fetchContacts('');
                }}
                getOptionLabel={(option: any) => option?.name}
                options={options}
                renderInput={(params) => <TextField {...params} label="" />}
                onChange={(event, newValue) => {

                  if (!newValue) {
                    setFormData({
                      name: undefined,
                      email: undefined,
                      phone: undefined,
                      address: undefined,
                      roles: [],
                      service_provider: serviceProvider,
                    });
                    setExistingContact(false);
                  }else {
                    setFormData({...formData,
                      address: newValue?.address,
                      email: newValue?.email,
                      roles: newValue?.roles,
                      phone: newValue?.phone,
                      service_provider: serviceProvider,
                    });
                    setExistingContact(newValue);
                  }
                }}
                onInputChange={(e:any)=>{
                  setFormData({...formData, name:e.target.value});
                }}
              />
            </Stack>

            <Stack direction={'row'}>
              <Typography
                sx={{
                  width: '220px',
                }}
              >
                Email
              </Typography>
              <TextField
                variant="outlined"
                size={'small'}
                fullWidth
                value={formData?.email?formData?.email:''}
                onChange={(event) => {
                  handleChange('email', event.target.value);
                }}
              />
            </Stack>

            <Stack direction={'row'}>
              <Typography
                sx={{
                  width: '220px',
                }}
              >
                Phone
              </Typography>
              <TextField
                variant="outlined"
                size={'small'}
                value={formData?.phone?formData?.phone:''}
                fullWidth
                onChange={(event) => {
                  handleChange('phone', event.target.value);
                }}
              />
            </Stack>

            <Stack direction={'row'}>
              <Typography
                sx={{
                  width: '220px',
                }}
              >
                Address
              </Typography>
              <TextField
                variant="outlined"
                size={'small'}
                value={formData?.address?formData?.address:''}
                fullWidth
                onChange={(event) => {
                  handleChange('address', event.target.value);
                }}
              />
            </Stack>

            <Stack direction={'row'}>
              <Typography
                sx={{
                  width: '220px',
                }}
              >
                Roles
              </Typography>
              <Autocomplete
                size={'small'}
                multiple
                fullWidth={true}
                PopperComponent={LargePopper}
                onOpen={() => {
                  fetchRoles('');
                }}
                getOptionLabel={(option: any) => option?.name}
                options={roleOptions}
                value={formData.roles}
                renderInput={(params) => <TextField {...params} label="" />}
                onChange={(event, newValue) => {
                  handleChange('roles', newValue);
                }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <MUIButton
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </MUIButton>

          <LoadingButton
            loading={loading}
            variant="contained"
            onClick={() => send()}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
